import { Component, computed, inject, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, EMPTY, finalize, take, tap } from 'rxjs';
import { SendInfoDialog } from 'src/app/components/send-info-dialog/send-info-dialog';
import { ShipmentQrcode } from "src/app/components/shipment-qrcode/shipment-qrcode";
import { Shipment } from 'src/app/shared/models/shipment';
import { ShipmentRequest } from 'src/app/shared/models/shipment-request';
import { SnackbarPanelClass } from 'src/app/shared/models/snackbar-panel-config';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ShipmentsApiService } from 'src/app/shared/services/shipments-api.service';
import { SnackbarWithNavService } from 'src/app/shared/services/snackbar-with-nav.service';

@Component({
  selector: 'app-qr',
  imports: [
    RouterLink,
    MatButton,
    ShipmentQrcode
  ],
  templateUrl: './qr.component.html',
  styleUrl: './qr.component.scss'
})
export class QrComponent {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private snackBarService = inject(SnackbarWithNavService);
  private shipmentsApiService = inject(ShipmentsApiService);
  private authService = inject(AuthService);

  shipment = signal<Shipment | null>(null);
  displaySendInfo = computed(() => this.authService.isInRole('SendInfo'));

  sendInfoToCustomer = signal(false);
  userInstructions = computed(() => {
    if (this.sendInfoToCustomer()) {
      return "Skicka bild och länk till kund";
    } else {
      return "Visa QR-koden hos paketombudet";
    }
  });

  constructor() {
    this.route.paramMap.pipe(
      take(1),
      tap(params => {
        const shipmentId = params.get('shipmentId');
        const courier = params.get('courier');
        this.shipmentsApiService.getShipment(courier!, shipmentId!).pipe(
          tap(shipment => {
            this.shipment.set(shipment);
          })
        ).subscribe();
      })
    ).subscribe();
  }

  openSendInfoDialog(): void {
    const shipment = this.shipment();
    if (shipment == null) {
      return;
    }

    this.dialog.open(SendInfoDialog, {
      data: shipment,
      width: '400px',
      position: { top: '100px' }
    }).afterClosed().pipe(
      take(1),
      tap((result: ShipmentRequest | undefined) => {
        if (result) {
          this.shipmentsApiService.updateShipment(shipment.Courier, shipment.ShipmentId, result).pipe(
            take(1),
            tap((shipment) => {
              this.shipment.set(shipment);
            })
          ).subscribe();
          this.sendInfoToCustomer.set(true);
        }
      })
    ).subscribe();
  }

  cancelShipment(): void {
    const shipment = this.shipment()!;

    let message = 'Inlämning abruten!';
    this.shipmentsApiService.updateShipment(shipment.Courier, shipment.ShipmentId, {
      status: 'ABORTED'
    }).pipe(
      take(1),
      catchError(error => {
        message = "Fel vid inlämning!";
        return EMPTY;
      }),
      finalize(() => {
        this.snackBarService.show(message, '/home', 'error-snackbar');
      })
    ).subscribe();
  }

  submitShipment(): void {
    const shipment = this.shipment()!;

    let message = 'Försändelse inlämnad!';
    let panelClass: SnackbarPanelClass = 'success-snackbar';
    this.shipmentsApiService.updateShipment(shipment.Courier, shipment.ShipmentId, {
      status: 'USED'
    }).pipe(
      take(1),
      catchError(error => {
        message = "Fel vid inlämning!";
        panelClass = 'error-snackbar';
        return EMPTY;
      }),
      finalize(() => {
        this.snackBarService.show(message, '/home', panelClass);
      })
    ).subscribe();
  }
}
